import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Terminal, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type * as S from "@releo/shared";
import { Button, ErrorBox, Field, Input, Select, Spinner } from "@/components/ui";
import { ApiError, http } from "@/lib/api";
import { useMe, useOrgs } from "@/lib/queries";
import { AuthCard } from "./auth";

const normalize = (code: string) => code.toUpperCase().replace(/[^A-Z]/g, "");

export function CliLoginPage({ code: initialCode }: { code?: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const [code, setCode] = useState(initialCode ?? "");
  const userCode = normalize(code);
  const ready = !!me.data && userCode.length === 8;
  const login = useQuery({
    queryKey: ["cli-login", userCode],
    queryFn: () => http.get<S.CliLogin>(`/cli/login/${userCode}`),
    enabled: ready,
    retry: false,
  });
  const orgs = useOrgs();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [decision, setDecision] = useState<"approved" | "denied" | null>(null);

  if (me.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!me.data) {
    const redirect = `/cli${initialCode ? `?code=${encodeURIComponent(initialCode)}` : ""}`;
    return (
      <AuthCard title={t("cli.title")} subtitle={t("cli.signInSubtitle")}>
        <Link to="/login" search={{ redirect } as never} className="block">
          <Button className="w-full justify-center">{t("cli.signIn")}</Button>
        </Link>
      </AuthCard>
    );
  }

  if (decision) {
    const approved = decision === "approved";
    return (
      <AuthCard title={t(approved ? "cli.approvedTitle" : "cli.deniedTitle")}>
        <div className="flex items-start gap-3 text-sm">
          {approved ? <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" /> : <XCircle className="h-5 w-5 shrink-0 text-fg-muted" />}
          <p>{t("cli.backToTerminal")}</p>
        </div>
      </AuthCard>
    );
  }

  const organizations = orgs.data ?? [];
  const selectedOrg = orgId ?? me.data.organization?.id ?? organizations[0]?.id ?? "";
  const decide = async (action: "approve" | "deny") => {
    setBusy(true);
    setError(null);
    try {
      await http.post(`/cli/login/${userCode}/${action}`, action === "approve" ? { organizationId: selectedOrg } : undefined);
      setDecision(action === "approve" ? "approved" : "denied");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const pending = login.data?.status === "pending";
  let status: React.ReactNode = null;
  if (ready && login.isLoading) status = <Spinner className="h-5 w-5" />;
  else if (login.error) {
    status = <ErrorBox error={login.error instanceof ApiError && login.error.status === 404 ? new Error(t("cli.expired")) : login.error} />;
  } else if (login.data && !pending) status = <ErrorBox error={new Error(t("cli.used"))} />;

  return (
    <AuthCard title={t("cli.title")} subtitle={t("cli.subtitle")}>
      <div className="space-y-4">
        <Field label={t("cli.code")} htmlFor="cli-code">
          <Input
            id="cli-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="XXXX-XXXX"
            autoComplete="off"
            spellCheck={false}
            className="font-mono uppercase tracking-widest"
          />
        </Field>
        {status}
        {pending && login.data && (
          <>
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
              <Terminal className="h-4 w-4 shrink-0 text-fg-muted" />
              <span className="min-w-0 break-words font-medium">{login.data.clientName}</span>
              <span className="ml-auto font-mono text-fg-muted">{login.data.userCode}</span>
            </div>
            {organizations.length > 1 && (
              <Field label={t("cli.organization")} htmlFor="cli-org">
                <Select id="cli-org" value={selectedOrg} onChange={(e) => setOrgId(e.target.value)}>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <p className="text-sm text-fg-muted">{t("cli.warning")}</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 justify-center" disabled={busy} onClick={() => void decide("deny")}>
                {t("cli.deny")}
              </Button>
              <Button className="flex-1 justify-center" loading={busy} disabled={!selectedOrg} onClick={() => void decide("approve")}>
                {t("cli.approve")}
              </Button>
            </div>
          </>
        )}
        <ErrorBox error={error} />
      </div>
    </AuthCard>
  );
}
